import { Component } from '@theme/component';
import { fetchConfig, debounce, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Cell Devices (CXG1012-04): each cart change waits for the one before it (updateQuantity). @type {Promise<void>} */
  #changeQueue = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      key: this.refs.cartItemRows[line - 1]?.dataset.key || undefined,   // Cell Devices (W4 review) — by key, as a removal is
      quantity,
      action: 'change',
      // Cell Devices (CXG1012-05, Codex 12 Oct) — a change the server never confirmed, and the re-read could not settle,
      // puts the quantity back to the one the prices on screen are for; the line then says so (#showUnconfirmed). A
      // CONFIRMED change keeps the new quantity: the server holds it, only the drawing failed.
      onFail: (confirmed) => {
        if (confirmed) return;
        const input = this.refs.quantitySelectors[line - 1]?.querySelector('input');
        if (input) input.value = input.defaultValue;
      },
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    const rowsToRemove = cartItemRowToRemove
      ? [
          cartItemRowToRemove,
          // Get all nested lines of the row to remove
          ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
        ]
      : [];

    // Cell Devices (WEB-036, 2026-09-19) — HIDE the row, never DELETE it here. This used to call
    // row.remove() when the removal animation ended. The cart's own re-render (morphSection) matches rows
    // by tag, not by line key, so when the server's answer arrived first it REUSED the node being removed
    // for the surviving line; the class change cancelled the animation, the callback fired, and it deleted
    // the survivor — the cart said "1 item, £279" over an empty list until a reload. The server's section
    // render is now the only thing that removes a line: the collapse animation ends at height 0 and holds
    // (`forwards`), reduced motion hides the row outright, and a refused or unreadable answer brings the
    // rows back, because the cart still holds them.
    // A removal already in flight is not started twice (review, WEB-036): with the row kept until the
    // server answers, a keyboard user pressing Enter again on the same Remove button sent a second
    // `line` request that removed the NEXT line. A pending row is also `inert`, so it can neither be
    // activated nor keep focus; morph drops the attribute when it reuses the node.
    if (cartItemRowToRemove && cartItemRowToRemove.dataset.cdRemoving === '1') return;
    const hadFocus = !!cartItemRowToRemove && cartItemRowToRemove.contains(document.activeElement);
    rowsToRemove.forEach((row) => {
      row.dataset.cdRemoving = '1';
      row.inert = true;
      if (prefersReducedMotion()) {
        // `hidden` loses to the row's own `display: grid`; an inline style wins, and morph strips it.
        row.style.display = 'none';
        return;
      }
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });
    // Focus leaves the inert row for the cart itself — never for the next line's Remove button, where a
    // repeated Enter would remove the wrong item.
    if (hadFocus) {
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');
      this.focus({ preventScroll: true });
    }
    const restoreRows = () => rowsToRemove.forEach((row) => {
      if (!row.isConnected) return;
      row.inert = false;
      delete row.dataset.cdRemoving;
      row.style.removeProperty('display');
      row.classList.remove('removing');
      row.style.removeProperty('--row-height');
    });

    this.updateQuantity({
      line,
      // Cell Devices (W4, v494/W3 independent watch) — name the line by its KEY, not its position. `line` is
      // the 1-based position in the cart AS IT IS NOW: after a removal that committed but whose answer was
      // lost, the old "line 1" is the survivor, and removing it again by position removed the wrong item.
      // A key that is no longer in the cart is refused instead.
      key: cartItemRowToRemove?.dataset.key || undefined,
      quantity: 0,
      action: 'clear',
      onFail: restoreRows,
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {(confirmed?: boolean) => void} [config.onFail] - Cell Devices (WEB-036): undo an optimistic hide when the change did not happen (CXG1012-05: told whether the server had confirmed it).
   * @param {string} [config.key] - Cell Devices (W4): the line-item key; when given the change names the line by key, not position.
   */
  updateQuantity(config) {
    // Cell Devices (CXG1012-04, Codex 12 Oct) — ONE CHANGE AT A TIME, IN THE ORDER MADE. While a change saves the cart is
    // disabled for the pointer only (`.cart-items-disabled` is pointer-events), so a keyboard user could Shift+Tab back
    // into the quantity and send a second change while the first was out; each reply then drew its own cart, and an older
    // reply landing last put the older quantity and total back over the newer one the server held. Each change now waits
    // for the one before it (its reply and any re-read), so the server sees them in order and the last cart drawn is the
    // newest. A change is absolute (a quantity, or a removal by key), so waiting never changes what it does.
    const run = () => this.#sendChange(config);
    this.#changeQueue = this.#changeQueue.then(run, run).catch((error) => console.error(error));
    return this.#changeQueue;
  }

  /**
   * Cell Devices (CXG1012-04) — sends one change and draws its answer: the body that was updateQuantity's, now queued by it.
   * @param {Object} config - As updateQuantity's.
   * @returns {Promise<void>} Settles once the answer, or the re-read after a failure, has been drawn.
   */
  #sendChange(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(config.key ? { id: config.key } : { line: line }),   // Cell Devices (W4) — a key when we have one; see onLineItemRemove
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    let confirmed = false;   // Cell Devices (WEB-036 review): the server has accepted the change

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          config.onFail?.();   // Cell Devices (WEB-036) — the line is still in the cart; show it again
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        confirmed = true;

        // Cell Devices (CXG1012-06, Codex 12 Oct) — A SAVED CHANGE CAN COME BACK WITHOUT ITS SECTION. Shopify may fail to
        // render a section after the cart has changed and still answer 200 with that section null. It reached the parser
        // as the text "null", the count fell to 0, and morphSection (async, never awaited) rejected outside this chain, so
        // its recovery below never ran and the old line prices and total stayed. Now the count falls back to the cart's
        // own item_count, and the morph is awaited: a section missing from the answer, or not the cart's, fails it into
        // the catch below, which reads the cart section again from the server (the change is confirmed, never put back).
        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = new DOMParser().parseFromString(typeof sectionHTML === 'string' ? sectionHTML : '', 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : Number(parsedResponseText.item_count) || 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        return morphSection(this.sectionId, sectionHTML).then(() => this.#updateCartQuantitySelectorButtonStates());
      })
      .catch((error) => {
        // Cell Devices (WEB-036) — AFTER the server confirmed (e.g. the section HTML was missing and the morph
        // threw), the line really is gone: re-render the section from the server instead of resurrecting it.
        // W4 (v494/W3 independent watch) — and an AMBIGUOUS failure (no answer, an unreadable one) is not a
        // refusal: the server may have committed the change and lost the reply. So ask the SERVER what the
        // cart holds and render that; only if that read fails too do the rows come back locally (and since a
        // removal now names its line by key, a retry of a line already gone is refused, never another line).
        // A definite refusal (the server's `errors`) still restores at once, in the success path above.
        // (W4 review: renderSection does not await its morph. Since W6 the renderer rejects an HTTP error rather
        // than rendering its page, but the re-read here still awaits the morph, which throws on any answer that is
        // not the cart section — and only then restores.)
        // Cell Devices (CXG1012-06) — confirmed or not, the re-read AWAITS its morph (renderSection does not, so a section
        // missing from its answer rejected unseen), and it is returned, so the next queued change waits for it (CXG1012-04).
        // Cell Devices (CXG1012-05, Codex 12 Oct) — and when the re-read fails too, the line SAYS so. It used to keep the
        // new quantity beside the old prices, still shimmering, with no word, and Checkout open.
        console.error(error);
        return sectionRenderer.getSectionHTML(this.sectionId, false)
          .then((html) => morphSection(this.sectionId, html))
          .catch(() => {
            config.onFail?.(confirmed);
            this.#showUnconfirmed(line, confirmed);
          });
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Cell Devices (CXG1012-05) — the line says its change could not be shown as saved, and the shimmer stops.
   * @param {number} line - The line.
   * @param {boolean} confirmed - Whether the server had confirmed the change.
   */
  #showUnconfirmed(line, confirmed) {
    resetShimmer(this);
    const box = this.refs[`cartItemErrorContainer-${line}`];
    const text = this.refs[`cartItemError-${line}`];
    if (!(box instanceof HTMLElement) || !(text instanceof HTMLElement)) return;
    text.textContent = confirmed
      ? 'That change was saved, but your cart could not be shown again here. Please refresh the page.'
      : 'We could not confirm that change. Please refresh the page to see your cart.';
    box.classList.remove('hidden');
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});   // W6 — a failed refresh leaves the rows as they are
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});   // W6 — a failed refresh leaves the rows as they are
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
